import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sparkles, Ticket } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'
import { api, type StampArEventBlock, type StampEventGroup } from '../services/api'
import { useStore } from '../store/useStore'
import { arWebArUrl } from '../lib/features'
import { gotoWebAr } from '../lib/arLaunch'
import LotteryResultCard from './LotteryResultCard'
import PrizeStockBadge from './PrizeStockBadge'
import CollectedCharacters from './CollectedCharacters'
import StampProgress from './StampProgress'
import StampProgramInfoButton from './StampProgramInfoButton'
import StampProgramPanel from './StampProgramPanel'
import { showToast } from './Toast'

const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect'

/**
 * 外部アクティビティの企画 (#1154/#1159)。QRラリーとは独立した進捗・チケットを持つ。
 * 見出し/ボタン文言/起動URL は企画の設定 (activityLabel/ctaLabel/activityUrl)
 * 駆動で、type='ar' は未設定時に AR宝探しの既定値へフォールバックする。
 * 起動URL が解決できない場合は何も描画しない (デプロイレベルの安全弁)。
 *
 * #1898 **イベントのカードの中に並ぶ企画のひとつ**として描く。紺ヘッダ (開閉) と
 * 開催期間はイベント側が持つので、ここは企画の見出しと中身だけ。
 */
export default function ActivityCard({
  arEvent,
  group,
  open,
  onToggle,
}: {
  arEvent: StampArEventBlock
  /** 所属するイベント。企画名・説明がイベントと同じなら繰り返さない。 */
  group?: Pick<StampEventGroup, 'name' | 'description'>
  /** 企画ごとの開閉 (状態はイベントのカード側が持つ) */
  open: boolean
  onToggle: () => void
}) {
  const navigate = useNavigate()
  const { t } = useI18n()
  const isLoggedIn = useStore((s) => s.isLoggedIn)
  const [launching, setLaunching] = useState(false)

  const { event, prize, progress, lottery, myDraw } = arEvent
  // 進捗を記録しない外部企画 (例: 神経衰弱)。外部アプリがクリアを知らせてこないので、
  // スタンプ枠を出すと「0/1 のまま増えない」ように見える。説明と起動ボタンだけにする。
  const linkOnly = !!event.linkOnly
  const achieved = progress?.achieved ?? false
  const isAr = (event.type ?? 'qr') === 'ar'
  // 設定駆動 + AR 既定値フォールバック (#1159)
  const launchUrl = event.activityUrl || (isAr ? arWebArUrl() : '')
  const heading = event.activityLabel || (isAr ? t('stampRally.arTitle') : event.name)
  const cta =
    event.ctaLabel ||
    (isAr ? t('stampRally.arLaunchCta') : linkOnly ? t('stampRally.linkActivityCta') : t('stampRally.activityLaunchCta'))
  // 説明はイベントと同じなら繰り返さない (企画名と種類は見出しに必ず出す)
  const showDescription = !!event.description && event.description !== group?.description

  // WebAR からブラウザ戻るで bfcache 復元されると launching=true のまま残り
  // ボタンが「起動中…」で固まるため、復元時にリセットする
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setLaunching(false)
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [])

  const handleLaunch = async () => {
    if (!isLoggedIn) {
      sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, '/stamp-rally')
      navigate('/login')
      return
    }
    setLaunching(true)
    try {
      const { token } = await api.launchArSession({ eventId: event.id })
      gotoWebAr(token, event.id, undefined, launchUrl) // 画面ごと外部アプリへ遷移
    } catch {
      showToast({ type: 'error', message: t('stampRally.arLaunchError') })
      setLaunching(false)
    }
  }

  if (!launchUrl) return null

  return (
    <StampProgramPanel
      icon={<Sparkles size={18} />}
      label={heading}
      name={event.name}
      description={showDescription ? event.description : ''}
      tone="gold"
      // リンクで遊ぶだけの企画は進捗を持たない
      summary={linkOnly ? undefined : `${progress?.stampCount ?? 0}/${event.stampGoal}`}
      open={open}
      onToggle={onToggle}
    >

      {linkOnly ? (
        <>
          {/* ログインもトークンも要らない。**別タブで開く** — クリア画面を見せたあと
              アプリに戻ってこられるように。<a> にしているのは、ポップアップ
              ブロックに掛からず、長押しでリンクを共有もできるため。 */}
          <a
            href={launchUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 rounded-full bg-yokoso-red text-white py-3.5 text-sm font-body font-medium tap-effect"
          >
            <Sparkles size={18} />
            {cta}
          </a>
          <p className="text-center text-xs font-body text-text-tertiary -mt-2">
            {t('stampRally.linkActivityHint')}
          </p>
        </>
      ) : (
        <>
          <StampProgress count={progress?.stampCount ?? 0} goal={event.stampGoal} />
          {prize && <PrizeStockBadge prize={prize} lottery={lottery} />}
          {/* #1871 引いた後は自分の結果 (残数はカードの中) */}
          {myDraw && <LotteryResultCard draw={myDraw} />}

          {/* 達成しても**探すのをやめさせない**。目標よりスポット数の方が多く、
              残りのキャラを見つける楽しみが残っている。以前は達成した時点で
              起動ボタンがチケットに差し替わり、AR に戻れなくなっていた。 */}
          {achieved && (
            <button
              onClick={() => navigate(`/stamp-rally/ticket?eventId=${encodeURIComponent(event.id)}`)}
              className="flex items-center justify-center gap-2 rounded-full bg-gold text-white py-3.5 text-sm font-body font-medium tap-effect"
            >
              <Ticket size={18} />
              {t('stampRally.goToTicket')}
            </button>
          )}
          <button
            onClick={() => void handleLaunch()}
            disabled={launching}
            className={`flex items-center justify-center gap-2 rounded-full py-3.5 text-sm font-body font-medium tap-effect disabled:opacity-60 ${
              achieved
                ? 'border border-yokoso-red text-yokoso-red'
                : 'bg-yokoso-red text-white'
            }`}
          >
            <Sparkles size={18} />
            {launching ? t('stampRally.arLaunching') : achieved ? t('stampRally.arKeepHunting') : cta}
          </button>
          {!achieved && (
            <p className="text-center text-xs font-body text-text-tertiary -mt-2">
              {isLoggedIn ? t('stampRally.arLaunchHint') : t('stampRally.arLoginRequired')}
            </p>
          )}

          {/* 何を集めたかは**達成後も見られる**。ここで消えると、キャラが記録に残らない */}
          <CollectedCharacters eventId={event.id} />
        </>
      )}

      <StampProgramInfoButton programId={event.id} />
    </StampProgramPanel>
  )
}
